import { Component, input, computed, signal, effect } from '@angular/core';
import { PaginationState } from '../../../core/models/common/pageable.model';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../button/button.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export type OnPaginationChange = (page: number, pageSize: number) => void;

export interface QueryParamConfig {
  page: string;
  pageSize: string;
}

@Component({
  selector: 'app-pagination',
  imports: [TranslateModule, ButtonComponent, CommonModule, FormsModule],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
})
export class PaginationComponent {
  readonly paginationState = input.required<PaginationState>();
  readonly onPaginationChange = input.required<OnPaginationChange>();
  /** Number of page buttons to show in the middle (default 5). E.g. "Anterior 1 ... 4 5 6 ... 9 Próximo" */
  readonly visiblePages = input<number>(5);
  /** 'full' = page numbers + prev/next, 'simple' = only prev/next (for cursor-based APIs) */
  readonly displayMode = input<'full' | 'input' | 'simple'>('input');
  /** Sync page/pageSize with URL query params. When true, updates ?page and ?pageSize on change (default true) */
  readonly syncQueryParams = input<boolean>(true);
  /** Query param keys (default: page, pageSize) */
  readonly queryParamKeys = input<QueryParamConfig>({ page: 'page', pageSize: 'pageSize' });

  readonly pageInputValue = signal<string>('');

  readonly pageSizes = [10, 20, 50, 100];

  constructor() {
    effect(() => {
      const state = this.paginationState();
      const page1 = (state?.currentPage ?? 0) + 1;
      this.pageInputValue.set(String(page1));
    });
  }

  readonly totalPages = computed(() => {
    const state = this.paginationState();
    const total = state?.totalItems ?? 0;
    const size = state?.itemsPerPage ?? 10;
    return size > 0 ? Math.ceil(total / size) : 0;
  });

  /** Array of page numbers (1-based) or 'ellipsis' for display */
  readonly visiblePageNumbers = computed(() => {
    const state = this.paginationState();
    const total = this.totalPages();
    const currentPage1 = (state?.currentPage ?? 0) + 1;
    const visible = this.visiblePages();

    if (total <= 0) return [];
    if (total <= visible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const windowSize = Math.max(1, visible - 2);
    const half = Math.floor((windowSize - 1) / 2);
    let windowStart = Math.max(1, currentPage1 - half);
    let windowEnd = Math.min(total, windowStart + windowSize - 1);
    if (windowEnd - windowStart + 1 < windowSize) {
      windowStart = Math.max(1, windowEnd - windowSize + 1);
    }

    const result: (number | 'ellipsis')[] = [];
    if (windowStart > 1) {
      result.push(1);
      if (windowStart > 2) result.push('ellipsis');
    }
    for (let p = windowStart; p <= windowEnd; p++) {
      result.push(p);
    }
    if (windowEnd < total) {
      if (windowEnd < total - 1) result.push('ellipsis');
      result.push(total);
    }
    return result;
  });

  readonly startItem = computed(() => {
    const state = this.paginationState();
    const page = state?.currentPage ?? 0;
    const size = state?.itemsPerPage ?? 10;
    const count = state?.currentPageItems ?? 0;
    return count > 0 ? page * size + 1 : 0;
  });

  readonly endItem = computed(() => {
    const state = this.paginationState();
    const page = state?.currentPage ?? 0;
    const size = state?.itemsPerPage ?? 10;
    const count = state?.currentPageItems ?? 0;
    return count > 0 ? page * size + count : 0;
  });

  onChangePagination(page: number, pageSize: number): void {
    this.onPaginationChange()?.(page, pageSize);
  }

  goToPage(page1: number): void {
    const state = this.paginationState();
    this.onChangePagination(page1 - 1, state?.itemsPerPage ?? 10);
  }

  submitPageInput(): void {
    const raw = this.pageInputValue().toString().trim();
    const num = parseInt(raw, 10);
    const total = this.totalPages();
    if (!Number.isFinite(num) || num < 1 || num > total) return;
    this.goToPage(num);
  }
}
